import { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleOf, ROLE_LABELS } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Save, Loader2, Camera, Trash2, UserCircle } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const role = roleOf(user);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: user?.name || "", title: user?.title || "", phone: user?.phone || "",
    bio: user?.bio || "", expertise: user?.expertise || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const avatarUrl = user?.avatar_file_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${user.avatar_file_id}?v=${user.avatar_updated_at || ""}`
    : null;
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name cannot be empty."); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, title: form.title, phone: form.phone, bio: form.bio };
      if (role === "supervisor") payload.expertise = form.expertise;
      await api.patch("/profile", payload);
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (maximum 5MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      await api.delete("/profile/avatar");
      await refresh();
      toast.success("Profile photo removed");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl animate-fade-up" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">My profile</h1>
        <p className="text-muted-foreground mt-1">Update your details and profile photo.</p>
      </div>

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <Avatar className="h-24 w-24 brand-bg" data-testid="profile-avatar">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={user?.name} />}
            <AvatarFallback className="brand-bg text-white text-2xl">{initials}</AvatarFallback>
          </Avatar>
          <div className="space-y-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid="avatar-file-input" onChange={onPickFile} />
            <div className="flex gap-2">
              <Button data-testid="avatar-upload-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />}Upload photo
              </Button>
              {user?.avatar_file_id && (
                <Button data-testid="avatar-remove-btn" variant="ghost" onClick={removePhoto} disabled={uploading} className="text-red-500 hover:text-red-700">
                  <Trash2 className="h-4 w-4 mr-1" />Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">PNG, JPG, WEBP or GIF · up to 5MB.</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2"><UserCircle className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Personal details</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Full name</Label><Input data-testid="profile-name" value={form.name} onChange={(e) => upd("name", e.target.value)} /></div>
          <div><Label className="text-xs">Title (optional)</Label><Input data-testid="profile-title" value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="e.g. Dr, Prof, Ms" /></div>
          <div><Label className="text-xs">Email</Label><Input data-testid="profile-email" value={user?.email || ""} disabled className="bg-muted/50" /></div>
          <div><Label className="text-xs">Phone (optional)</Label><Input data-testid="profile-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="e.g. +255 700 000 000" /></div>
          <div><Label className="text-xs">Role</Label><Input value={ROLE_LABELS[role] || role || ""} disabled className="bg-muted/50" /></div>
        </div>
        {role === "supervisor" && (
          <div><Label className="text-xs">Areas of expertise</Label><Textarea data-testid="profile-expertise" rows={2} value={form.expertise} onChange={(e) => upd("expertise", e.target.value)} placeholder="e.g. Public health, Epidemiology, Health policy" /><p className="text-xs text-muted-foreground mt-1">Used by the AI best-fit supervisor recommendation.</p></div>
        )}
        <div><Label className="text-xs">Bio (optional)</Label><Textarea data-testid="profile-bio" rows={3} value={form.bio} onChange={(e) => upd("bio", e.target.value)} placeholder="A short introduction about yourself" /></div>
        <Button data-testid="profile-save" onClick={save} disabled={saving} className="brand-bg text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save changes</>}
        </Button>
      </Card>
    </div>
  );
}
